/**
 * Small UPS Configuration Types (Frontend)
 * TypeScript interfaces and Zod schemas for the repeating Small UPS section (#996 Phase 1)
 *
 * IMPORTANT: Zod schemas must be wrapped in useMemo() for iOS to avoid crashes
 * @see GitLab #996 — Repeating Sections (Phase 1: Small UPS)
 *
 * Contract note: status/name use the GENERIC repeating-section column names
 * `status`/`name` (NOT `smallUpsStatus`/`smallUpsName`). The rack version used
 * `rackStatus`/`rackName`; for Small UPS use `status`/`name`.
 */

import { z } from 'zod';

export type SmallUpsStatus = 'existing' | 'new';

/**
 * Small UPS Configuration interface (camelCase — matches API response)
 */
export interface SmallUpsConfiguration {
  id?: number;
  requestId: number;

  // Existing vs New status — generic repeating-section field (#996)
  status: SmallUpsStatus;

  // User-defined instance name (auto-named "UPS N" server-side when omitted)
  name?: string;

  /** Facility location tag assignment (#999) */
  locationTagId?: number | null;

  // Per-instance Small UPS fields (16) — keys mirror the live UI dataField keys
  smallUpsManufacturer?: string;
  smallUpsModel?: string;
  powerSmallUpsDescription?: string;
  smallUpsSerialNumber?: string;
  powerTypesOfUps?: string;
  smallUpsInputVoltage?: string;
  smallUpsInputPlug?: string;
  powerInputPowerPhase?: string;
  smallUpsCapacity?: number;
  smallUpsLoadPercentage?: number;
  powerRuntime?: string;
  smallUpsBatteryCabinetQty?: number;
  smallUpsMounting?: string;
  smallUpsMountType?: string;
  smallUpsOutlets?: string;
  smallUpsMaintenanceBypassRequired?: boolean;
  smallUpsExpectedLoadGrowth?: number;
  smallUpsLoadOnGenerator?: boolean;
  smallUpsRequiredSensors?: string;
  smallUpsBackupGenerator?: boolean;
  powerSmallUpsAdditionalInfo?: string;

  // Metadata
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Input for creating a new small UPS configuration
 */
export type CreateSmallUpsConfigurationInput = Omit<
  SmallUpsConfiguration,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Input for updating a small UPS configuration
 */
export type UpdateSmallUpsConfigurationInput = Partial<
  Omit<SmallUpsConfiguration, 'id' | 'requestId' | 'createdAt' | 'updatedAt'>
>;

/**
 * Grouped small UPS configurations for the segmented view (existing vs new)
 */
export interface GroupedSmallUpsConfigurations {
  existing: SmallUpsConfiguration[];
  new: SmallUpsConfiguration[];
}

/**
 * API response for small UPS configurations list
 */
export interface SmallUpsConfigurationsResponse {
  smallUps: SmallUpsConfiguration[];
  total: number;
}

/**
 * Zod schema factory for small UPS configuration validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 *
 * @example
 * const schema = useMemo(() => createSmallUpsConfigurationSchema(), []);
 */
export const createSmallUpsConfigurationSchema = () =>
  z.object({
    id: z.number().optional(),
    requestId: z.number(),
    status: z.enum(['existing', 'new']),

    // User-defined instance name
    name: z.string().optional(),
    locationTagId: z.number().int().nullable().optional(),

    // Per-instance fields
    smallUpsManufacturer: z.string().optional(),
    smallUpsModel: z.string().optional(),
    powerSmallUpsDescription: z.string().optional(),
    smallUpsSerialNumber: z.string().optional(),
    powerTypesOfUps: z.string().optional(),
    smallUpsInputVoltage: z.string().optional(),
    smallUpsInputPlug: z.string().optional(),
    powerInputPowerPhase: z.string().optional(),
    smallUpsCapacity: z.number().min(0).optional(),
    smallUpsLoadPercentage: z.number().min(0).max(100).optional(),
    powerRuntime: z.string().optional(),
    smallUpsBatteryCabinetQty: z.number().min(0).optional(),
    smallUpsMounting: z.string().optional(),
    smallUpsMountType: z.enum(['Wall', 'Floor', 'Rack']).optional(),
    smallUpsOutlets: z.string().optional(),
    smallUpsMaintenanceBypassRequired: z.boolean().optional(),
    smallUpsExpectedLoadGrowth: z.number().min(0).optional(),
    smallUpsLoadOnGenerator: z.boolean().optional(),
    smallUpsRequiredSensors: z.string().optional(),
    smallUpsBackupGenerator: z.boolean().optional(),
    powerSmallUpsAdditionalInfo: z.string().optional(),

    // Metadata
    displayOrder: z.number().min(0),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

/**
 * Zod schema factory for create small UPS input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const createSmallUpsInputSchema = () =>
  createSmallUpsConfigurationSchema().omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

/**
 * Zod schema factory for update small UPS input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const updateSmallUpsInputSchema = () =>
  createSmallUpsConfigurationSchema()
    .omit({
      id: true,
      requestId: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial();

/**
 * Type inference from Zod schemas
 */
export type SmallUpsConfigurationSchema = z.infer<
  ReturnType<typeof createSmallUpsConfigurationSchema>
>;
export type CreateSmallUpsInputSchema = z.infer<
  ReturnType<typeof createSmallUpsInputSchema>
>;
export type UpdateSmallUpsInputSchema = z.infer<
  ReturnType<typeof updateSmallUpsInputSchema>
>;

/** Status options for small UPS instances. */
export const SMALL_UPS_STATUS_OPTIONS: SmallUpsStatus[] = ['existing', 'new'];

/**
 * Default values for a new small UPS configuration
 */
export const DEFAULT_SMALL_UPS_CONFIGURATION: Partial<SmallUpsConfiguration> = {
  status: 'new',
  displayOrder: 0,
};
